/**
 * Racing, as a pure state machine.
 *
 * One player, one car, one track, and a stopwatch. Nobody else is on the track:
 * everyone races alone and the fastest time wins, so the only thing that leaves
 * the browser is the finishing time (migrations/0062_racing_game.sql). The rules
 * live HERE rather than in the page so they can be asserted without a canvas —
 * `scripts/race-game-check.mjs` drives them through `step()`.
 *
 * Every function returns a new state and never mutates its argument. The page
 * holds the state in a ref and redraws it each animation frame.
 */

/** The playfield, in world units. The page draws it at a quarter of this. */
export const WIDTH = 800
export const HEIGHT = 500

/**
 * The one track: the centre line, as a closed loop driven in this order. The
 * car starts on the first segment heading for the second point, and the finish
 * line is at the first point.
 *
 * Straights that run side by side are kept well over a track width apart. Lap
 * counting goes by the nearest segment, and two segments closer than that would
 * let a car sitting on one be counted as on the other.
 */
export const TRACK: ReadonlyArray<{ x: number; y: number }> = [
  { x: 420, y: 440 },
  { x: 130, y: 440 },
  { x: 70, y: 380 },
  { x: 70, y: 130 },
  { x: 130, y: 60 },
  { x: 330, y: 60 },
  { x: 390, y: 120 },
  { x: 390, y: 220 },
  { x: 450, y: 270 },
  { x: 520, y: 220 },
  { x: 520, y: 120 },
  { x: 580, y: 60 },
  { x: 680, y: 60 },
  { x: 740, y: 120 },
  { x: 740, y: 380 },
  { x: 680, y: 440 },
]
/** Half the width of the tarmac. Further than this from the centre line is grass. */
export const TRACK_HALF = 30

/** Laps in a race. A time is the time for all of them. */
export const LAPS = 3

/** Units per second. */
export const MAX_SPEED = 260
/** Speed gained per second with the gas held. */
export const ACCEL = 180
/** Speed lost per second with the brake held. */
export const BRAKE = 420
/** Speed lost per second with neither pedal down. */
export const COAST = 90
/**
 * The most a car can go with any wheel of its centre off the tarmac. Grass is a
 * crawl, the way it was on the 2600: leaving the track never ends the race, it
 * just costs time — which is what keeps cutting a corner from paying.
 */
export const OFF_TRACK_SPEED = 55
/** Radians per second at full steering. */
export const TURN_RATE = 3.2
/**
 * Below this speed steering is scaled down, so a car at rest cannot spin on the
 * spot — it has to be rolling to turn, like a car.
 */
export const STEER_FULL_SPEED = 60

/**
 * Faster than any race could be: under three laps at top speed along a line
 * thirty percent shorter than the centre line, which no line through these
 * corners is. The database refuses anything quicker (`race_times` CHECK in
 * 0062), because the time is sent by the browser and this is the one claim
 * about it the server can test. `race-game-check.mjs` asserts the two agree,
 * and that a real lap clears it.
 */
export const MIN_RACE_MS = 15000

/** See missile-game.ts: a frame is integrated in slices no longer than this. */
export const MAX_SLICE = 1 / 120
/**
 * A frame longer than this is treated as this long. A background tab stops
 * animation frames; without the cap, coming back would teleport the car. The
 * clock is the simulation's own, so a capped frame costs the player nothing.
 */
export const MAX_FRAME = 0.1

export type Status = 'ready' | 'racing' | 'finished'

export interface Input {
  up: boolean
  down: boolean
  left: boolean
  right: boolean
}

export const NO_INPUT: Input = { up: false, down: false, left: false, right: false }

export interface RaceState {
  status: Status
  x: number
  y: number
  /** Radians: 0 points right, PI/2 points DOWN the screen. */
  heading: number
  speed: number
  /** Laps completed. */
  lap: number
  /**
   * The segment the car has to reach next for the lap to count. Segments must
   * be visited in order, so a short cut across the grass that skips one leaves
   * this waiting until the car goes back for it.
   */
  nextSeg: number
  /** Seconds since the start, by the simulation's clock. */
  elapsed: number
  offTrack: boolean
}

/** Distance from (x, y) to segment i (TRACK[i] to TRACK[i + 1], wrapping). */
function segmentDistance(i: number, x: number, y: number): number {
  const a = TRACK[i]
  const b = TRACK[(i + 1) % TRACK.length]
  const dx = b.x - a.x
  const dy = b.y - a.y
  const t = Math.max(0, Math.min(1, ((x - a.x) * dx + (y - a.y) * dy) / (dx * dx + dy * dy)))
  return Math.hypot(x - (a.x + t * dx), y - (a.y + t * dy))
}

/** Which segment of the centre line is closest to (x, y). */
export function nearestSegment(x: number, y: number): number {
  let best = 0
  let bestD = Infinity
  for (let i = 0; i < TRACK.length; i++) {
    const d = segmentDistance(i, x, y)
    if (d < bestD) {
      bestD = d
      best = i
    }
  }
  return best
}

/** Is (x, y) on the tarmac? */
export function onTrack(x: number, y: number): boolean {
  return segmentDistance(nearestSegment(x, y), x, y) <= TRACK_HALF
}

/** A car on the grid, waiting for the start: just past the line, facing along the first segment. */
export function newRace(): RaceState {
  const a = TRACK[0]
  const b = TRACK[1]
  const heading = Math.atan2(b.y - a.y, b.x - a.x)
  return {
    status: 'ready',
    x: a.x + Math.cos(heading) * 25,
    y: a.y + Math.sin(heading) * 25,
    heading,
    speed: 0,
    lap: 0,
    nextSeg: 1,
    elapsed: 0,
    offTrack: false,
  }
}

/** Start (or restart) a race from the grid. */
export function start(): RaceState {
  return { ...newRace(), status: 'racing' }
}

/** The finishing time to store, in whole milliseconds. */
export function raceMs(state: RaceState): number {
  return Math.round(state.elapsed * 1000)
}

/** One slice: pedals, steering, move, then count progress. `dt` is at most MAX_SLICE. */
function slice(state: RaceState, dt: number, input: Input): RaceState {
  let speed = state.speed
  if (input.up) speed += ACCEL * dt
  if (input.down) speed -= BRAKE * dt
  if (!input.up && !input.down) speed -= COAST * dt
  // No reverse: down is a brake, as asked. With no walls a rolling car can
  // always steer its way out, so nothing needs one.
  speed = Math.max(0, Math.min(MAX_SPEED, speed))
  if (state.offTrack) speed = Math.min(speed, OFF_TRACK_SPEED)

  const steer = (input.right ? 1 : 0) - (input.left ? 1 : 0)
  const heading = state.heading + steer * TURN_RATE * dt * Math.min(1, speed / STEER_FULL_SPEED)

  const x = Math.max(0, Math.min(WIDTH, state.x + Math.cos(heading) * speed * dt))
  const y = Math.max(0, Math.min(HEIGHT, state.y + Math.sin(heading) * speed * dt))
  const elapsed = state.elapsed + dt

  let { lap, nextSeg } = state
  const seg = nearestSegment(x, y)
  if (seg === nextSeg) {
    // Reaching segment 0 in order is crossing the finish line.
    if (seg === 0) lap += 1
    nextSeg = (seg + 1) % TRACK.length
  }

  const next: RaceState = { ...state, x, y, heading, speed, elapsed, lap, nextSeg, offTrack: !onTrack(x, y) }
  return lap >= LAPS ? { ...next, status: 'finished', speed: 0 } : next
}

/**
 * Advance the race by `dt` seconds with these keys held. Does nothing unless a
 * race is on, so the page can call it every frame regardless of what is on screen.
 */
export function step(state: RaceState, dt: number, input: Input): RaceState {
  if (state.status !== 'racing') return state
  let remaining = Math.min(Math.max(0, dt), MAX_FRAME)
  let next = state
  while (remaining > 0 && next.status === 'racing') {
    const d = Math.min(remaining, MAX_SLICE)
    next = slice(next, d, input)
    remaining -= d
  }
  return next
}

/** 83456 → "1:23.456". */
export function formatRaceTime(ms: number): string {
  const m = Math.floor(ms / 60000)
  const s = Math.floor((ms % 60000) / 1000)
  return `${m}:${String(s).padStart(2, '0')}.${String(ms % 1000).padStart(3, '0')}`
}
